import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useSubscription } from '@apollo/client/react';
import { gql } from '@apollo/client';
// Note: amazon-chime-sdk-component-library-react needs to be installed
// npm install amazon-chime-sdk-component-library-react amazon-chime-sdk-js
// @ts-ignore - Package may not be installed yet
import { MeetingProvider } from 'amazon-chime-sdk-component-library-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { VideoCallWrapper } from '../components/video/VideoCallInterface';
import { ON_VIDEO_CONFERENCE_UPDATED } from '../graphql/queries';
import toast from 'react-hot-toast';

// GraphQL queries and mutations
const GET_VIDEO_CONFERENCE = gql`
  query GetVideoConferenceForGuest($id: ID!) {
    getVideoConferenceForGuest(id: $id) {
      id
      title
      status
      scheduledStartTime
      scheduledEndTime
      hostId
      host {
        id
        firstName
        lastName
      }
      guestName
      guestEmail
      isRecordingEnabled
      waitingRoomEnabled
    }
  }
`;

const JOIN_VIDEO_CONFERENCE_AS_GUEST = gql`
  mutation JoinVideoConferenceAsGuest($id: ID!, $guestName: String!, $guestEmail: String) {
    joinVideoConferenceAsGuest(id: $id, guestName: $guestName, guestEmail: $guestEmail) {
      status
      waitingParticipantId
      message
      meetingId
      mediaRegion
      attendeeId
      joinToken
      externalUserId
      meetingJson
      attendeeJson
    }
  }
`;

// Types
type ConferenceStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';

interface VideoConference {
  id: string;
  title: string;
  status: ConferenceStatus;
  scheduledStartTime: string;
  scheduledEndTime: string;
  hostId: string;
  host?: { id: string; firstName: string; lastName: string };
  guestName?: string;
  guestEmail?: string;
  clientId?: string;
  client?: { id: string; firstName: string; lastName: string };
  isRecordingEnabled: boolean;
  waitingRoomEnabled?: boolean;
}

interface ChimeMeetingCredentials {
  meetingId: string;
  mediaRegion: string;
  attendeeId: string;
  joinToken: string;
  externalUserId: string;
  meeting: any;
  attendee: any;
}

interface MediaDevice {
  deviceId: string;
  label: string;
}

// Page states
type PageState = 'loading' | 'pre-join' | 'waiting' | 'in-call' | 'ended' | 'error';

// Type definitions for Apollo queries/mutations
interface GetVideoConferenceForGuestData {
  getVideoConferenceForGuest: VideoConference;
}

interface JoinVideoConferenceAsGuestData {
  joinVideoConferenceAsGuest: {
    status?: string;
    waitingParticipantId?: string;
    message?: string;
    meetingId?: string;
    mediaRegion?: string;
    attendeeId?: string;
    joinToken?: string;
    externalUserId?: string;
    meetingJson?: string;
    attendeeJson?: string;
  };
}

interface OnVideoConferenceUpdatedData {
  onVideoConferenceUpdated: VideoConference;
}

export function VideoJoin() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  
  // Page state
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  // Helper to get stored guest info from localStorage
  const getStoredGuestInfo = useCallback(() => {
    if (!token) return { name: '', email: '' };
    try {
      const stored = localStorage.getItem(`guest-info-${token}`);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error reading guest info from localStorage:', e);
    }
    return { name: '', email: '' };
  }, [token]);
  
  // Helper to clear stored guest info
  const clearStoredGuestInfo = useCallback(() => {
    if (token) {
      try {
        localStorage.removeItem(`guest-info-${token}`);
      } catch (e) {
        console.error('Error clearing guest info from localStorage:', e);
      }
    }
  }, [token]);
  
  // Initialize from localStorage
  const storedGuestInfo = getStoredGuestInfo();
  
  // Form state - initialize from localStorage if available
  const [guestName, setGuestName] = useState(storedGuestInfo.name);
  const [guestEmail, setGuestEmail] = useState(storedGuestInfo.email);
  const [nameError, setNameError] = useState('');
  
  // Waiting room guest info (persisted in localStorage)
  const [waitingRoomGuestName, setWaitingRoomGuestName] = useState(storedGuestInfo.name);
  const [waitingRoomGuestEmail, setWaitingRoomGuestEmail] = useState(storedGuestInfo.email);
  
  // Persist guest info to localStorage when it changes
  useEffect(() => {
    if (token && waitingRoomGuestName) {
      try {
        localStorage.setItem(`guest-info-${token}`, JSON.stringify({
          name: waitingRoomGuestName,
          email: waitingRoomGuestEmail,
        }));
      } catch (e) {
        console.error('Error saving guest info to localStorage:', e);
      }
    }
  }, [token, waitingRoomGuestName, waitingRoomGuestEmail]);
  
  // Device state
  const [videoDevices, setVideoDevices] = useState<MediaDevice[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDevice[]>([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [audioLevel, setAudioLevel] = useState(0);
  
  // Video preview ref
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  
  // Meeting credentials
  const [credentials, setCredentials] = useState<ChimeMeetingCredentials | null>(null);
  
  // Track if this is a mobile staff user with pre-authenticated credentials
  const [isMobileStaff, setIsMobileStaff] = useState(false);
  
  // Track if user explicitly went back to pre-join
  const [userWentBack, setUserWentBack] = useState(false);

  // Check for mobile staff credentials in URL query params (passed from mobile app)
  // We use short param names to keep URL length minimal
  // Using useSearchParams from React Router for reliable SPA query param handling
  useEffect(() => {
    const checkMobileStaffCredentials = () => {
      try {
        console.log('[VideoJoin] Checking for mobile staff credentials...');
        console.log('[VideoJoin] searchParams:', searchParams.toString());
        console.log('[VideoJoin] window.location.href:', window.location.href);
        
        // Use React Router's searchParams (more reliable in SPA)
        const isStaff = searchParams.get('s') === '1';
        const meetingIdParam = searchParams.get('m');
        const attendeeIdParam = searchParams.get('a');
        const joinTokenParam = searchParams.get('t');
        
        console.log('[VideoJoin] Parsed params - s:', isStaff, 'm:', meetingIdParam, 'a:', attendeeIdParam, 't:', joinTokenParam ? 'present' : 'missing');
        
        if (isStaff && meetingIdParam && attendeeIdParam && joinTokenParam) {
          console.log('[VideoJoin] Mobile staff credentials detected, joining directly');
          
          // Build minimal meeting/attendee objects - Chime SDK will use these
          const meeting = {
            MeetingId: meetingIdParam,
            MediaRegion: 'us-east-1',
          };
          const attendee = {
            AttendeeId: attendeeIdParam,
            JoinToken: joinTokenParam,
          };
          
          setCredentials({
            meetingId: meetingIdParam,
            mediaRegion: 'us-east-1',
            attendeeId: attendeeIdParam,
            joinToken: joinTokenParam,
            externalUserId: 'mobile-staff',
            meeting,
            attendee,
          });
          setIsMobileStaff(true);
          setPageState('in-call');
          
          // Clear query params from URL for cleaner display
          window.history.replaceState(null, '', window.location.pathname);
          return true;
        }
        
        // Fallback: try hash (for backwards compatibility)
        const hash = window.location.hash.slice(1);
        if (hash) {
          const decoded = JSON.parse(atob(hash));
          if (decoded.isMobileStaff && decoded.meetingId && decoded.attendeeId && decoded.joinToken) {
            console.log('[VideoJoin] Mobile staff credentials detected from hash, joining directly');
            
            const meeting = decoded.meetingJson ? JSON.parse(decoded.meetingJson) : {
              MeetingId: decoded.meetingId,
              MediaRegion: decoded.mediaRegion || 'us-east-1',
            };
            const attendee = decoded.attendeeJson ? JSON.parse(decoded.attendeeJson) : {
              AttendeeId: decoded.attendeeId,
              JoinToken: decoded.joinToken,
            };
            
            setCredentials({
              meetingId: decoded.meetingId,
              mediaRegion: decoded.mediaRegion || 'us-east-1',
              attendeeId: decoded.attendeeId,
              joinToken: decoded.joinToken,
              externalUserId: decoded.externalUserId || 'mobile-user',
              meeting,
              attendee,
            });
            setIsMobileStaff(true);
            setPageState('in-call');
            window.history.replaceState(null, '', window.location.pathname);
            return true;
          }
        }
        return false;
      } catch (e) {
        console.log('[VideoJoin] Error checking mobile staff credentials:', e);
        return false;
      }
    };
    
    // Check immediately
    if (!checkMobileStaffCredentials()) {
      // If not found, try again after a short delay (in case of redirect/routing timing)
      const timer = setTimeout(() => {
        console.log('[VideoJoin] Retrying credential check after delay...');
        checkMobileStaffCredentials();
      }, 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [searchParams]);

  // Query conference details
  const { data, loading, error, refetch } = useQuery<GetVideoConferenceForGuestData>(GET_VIDEO_CONFERENCE, {
    variables: { id: token },
    skip: !token || isMobileStaff, // Skip query if mobile staff (already have credentials)
    fetchPolicy: 'network-only',
  });

  const conference: VideoConference | undefined = data?.getVideoConferenceForGuest;

  // Join mutation
  const [joinAsGuest, { loading: joining }] = useMutation<JoinVideoConferenceAsGuestData>(JOIN_VIDEO_CONFERENCE_AS_GUEST);

  // Initialize devices
  const initializeDevices = useCallback(async () => {
    try {
      // Request permissions first
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;

      // Get device list
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      const videoInputs = devices
        .filter(d => d.kind === 'videoinput')
        .map(d => ({ deviceId: d.deviceId, label: d.label || `Camera ${d.deviceId.slice(0, 4)}` }));
      
      const audioInputs = devices
        .filter(d => d.kind === 'audioinput')
        .map(d => ({ deviceId: d.deviceId, label: d.label || `Microphone ${d.deviceId.slice(0, 4)}` }));

      setVideoDevices(videoInputs);
      setAudioDevices(audioInputs);

      if (videoInputs.length > 0) {
        setSelectedVideoDevice(videoInputs[0].deviceId);
      }
      if (audioInputs.length > 0) {
        setSelectedAudioDevice(audioInputs[0].deviceId);
      }

      // Set up video preview
      if (videoRef.current && stream) {
        videoRef.current.srcObject = stream;
      }

      // Set up audio level monitoring
      setupAudioMonitoring(stream);
    } catch (err) {
      console.error('Error accessing media devices:', err);
    }
  }, []);

  // Audio level monitoring
  const setupAudioMonitoring = (stream: MediaStream) => {
    try {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      
      analyser.fftSize = 256;
      source.connect(analyser);
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      const updateLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          setAudioLevel(Math.min(100, average * 2));
        }
        requestAnimationFrame(updateLevel);
      };
      
      updateLevel();
    } catch (err) {
      console.error('Error setting up audio monitoring:', err);
    }
  };

  // Switch video device
  const switchVideoDevice = async (deviceId: string) => {
    setSelectedVideoDevice(deviceId);
    try {
      if (streamRef.current) {
        streamRef.current.getVideoTracks().forEach(track => track.stop());
      }
      
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: selectedAudioDevice ? { deviceId: { exact: selectedAudioDevice } } : true,
      });
      
      streamRef.current = newStream;
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
    } catch (err) {
      console.error('Error switching video device:', err);
    }
  };

  // Switch audio device
  const switchAudioDevice = async (deviceId: string) => {
    setSelectedAudioDevice(deviceId);
    try {
      if (streamRef.current) {
        streamRef.current.getAudioTracks().forEach(track => track.stop());
      }
      
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: selectedVideoDevice ? { deviceId: { exact: selectedVideoDevice } } : true,
        audio: { deviceId: { exact: deviceId } },
      });
      
      streamRef.current = newStream;
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
      setupAudioMonitoring(newStream);
    } catch (err) {
      console.error('Error switching audio device:', err);
    }
  };

  // Toggle camera
  const toggleCamera = () => {
    if (streamRef.current) {
      streamRef.current.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsCameraOn(!isCameraOn);
    }
  };

  // Toggle microphone
  const toggleMic = () => {
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsMicOn(!isMicOn);
    }
  };

  // Cleanup media streams
  const cleanupMedia = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  // Handle conference status changes
  useEffect(() => {
    // Never override 'ended' state - user has left or meeting has ended
    if (pageState === 'ended') {
      return;
    }
    
    // Only show loading state on initial load, not during refetch while in waiting room
    if (loading && pageState !== 'waiting' && pageState !== 'in-call') {
      setPageState('loading');
      return;
    }

    // Only show error if we don't have conference data
    // Ignore errors if we already have valid conference data (e.g., subscription errors)
    if (error && !conference) {
      console.error('GraphQL error loading conference:', error);
      setPageState('error');
      // Show more specific error message for debugging
      const errorMsg = error.message || 'Unknown error';
      if (errorMsg.includes('Not Authorized') || errorMsg.includes('Unauthorized')) {
        setErrorMessage('Authorization error. Please try refreshing the page.');
      } else if (errorMsg.includes('not found')) {
        setErrorMessage('This conference link is invalid or has expired.');
      } else {
        setErrorMessage(`Unable to load conference: ${errorMsg}`);
      }
      return;
    }

    if (!conference) {
      // Don't show error if we're still loading or in waiting room
      if (!loading && pageState !== 'waiting') {
        setPageState('error');
        setErrorMessage('This conference link is invalid or has expired.');
      }
      return;
    }

    switch (conference.status) {
      case 'SCHEDULED':
        // If we have stored guest info and user didn't go back, auto-restore to waiting room
        if (waitingRoomGuestName && !userWentBack && pageState !== 'waiting' && pageState !== 'in-call') {
          setPageState('waiting');
        } else if (pageState !== 'waiting' && pageState !== 'in-call' && pageState !== 'pre-join') {
          setPageState('pre-join');
        }
        break;
      case 'IN_PROGRESS':
        // If we have stored guest info and user didn't go back, auto-restore to waiting room (will auto-join)
        if (waitingRoomGuestName && !userWentBack && pageState !== 'waiting' && pageState !== 'in-call') {
          setPageState('waiting');
        } else if (pageState !== 'waiting' && pageState !== 'in-call' && pageState !== 'pre-join') {
          setPageState('pre-join');
        }
        break;
      case 'COMPLETED':
        clearStoredGuestInfo();
        setWaitingRoomGuestName('');
        setWaitingRoomGuestEmail('');
        setPageState('ended');
        setErrorMessage('This conference has ended.');
        break;
      case 'CANCELLED':
        clearStoredGuestInfo();
        setWaitingRoomGuestName('');
        setWaitingRoomGuestEmail('');
        setPageState('error');
        setErrorMessage('This conference has been cancelled.');
        break;
      case 'NO_SHOW':
        clearStoredGuestInfo();
        setWaitingRoomGuestName('');
        setWaitingRoomGuestEmail('');
        setPageState('error');
        setErrorMessage('This conference was marked as no-show.');
        break;
      default:
        if (pageState !== 'waiting' && pageState !== 'in-call' && pageState !== 'pre-join') {
          setPageState('pre-join');
        }
    }
  }, [conference, loading, error, pageState, waitingRoomGuestName, userWentBack, clearStoredGuestInfo]);

  // Initialize devices when entering pre-join
  useEffect(() => {
    if (pageState === 'pre-join') {
      initializeDevices();
    }
    return () => {
      if (pageState !== 'in-call') {
        cleanupMedia();
      }
    };
  }, [pageState, initializeDevices, cleanupMedia]);

  // Auto-join function for when meeting starts while in waiting room
  const autoJoinMeeting = async () => {
    // If no name stored, don't do anything - user needs to re-enter from pre-join
    if (!waitingRoomGuestName.trim()) {
      console.log('autoJoinMeeting: No guest name stored, skipping');
      return;
    }

    console.log('autoJoinMeeting: Attempting to join with name:', waitingRoomGuestName, 'email:', waitingRoomGuestEmail);

    try {
      const { data } = await joinAsGuest({
        variables: {
          id: token,
          guestName: waitingRoomGuestName.trim(),
          guestEmail: waitingRoomGuestEmail.trim(),
        },
      });

      console.log('autoJoinMeeting: Response:', data?.joinVideoConferenceAsGuest);

      if (data?.joinVideoConferenceAsGuest) {
        const response = data.joinVideoConferenceAsGuest;
        
        // Still in waiting room - stay on waiting screen
        if (response.status === 'WAITING') {
          console.log('autoJoinMeeting: Still in waiting room');
          // Don't change page state, just stay in waiting room
          return;
        }
        
        console.log('autoJoinMeeting: Got credentials, joining call');
        cleanupMedia();
        
        const meeting = response.meetingJson ? JSON.parse(response.meetingJson) : null;
        const attendee = response.attendeeJson ? JSON.parse(response.attendeeJson) : null;
        
        if (!meeting || !attendee || !response.meetingId || !response.attendeeId) {
          // Don't redirect, just log the error
          console.error('Failed to get meeting credentials');
          return;
        }
        
        toast.success('You have been admitted to the meeting!');
        setCredentials({
          meetingId: response.meetingId,
          mediaRegion: response.mediaRegion || 'us-east-1',
          attendeeId: response.attendeeId,
          joinToken: response.joinToken || '',
          externalUserId: response.externalUserId || '',
          meeting,
          attendee,
        });
        setPageState('in-call');
      }
    } catch (err: any) {
      console.error('Auto-join failed:', err);
      // Don't redirect to pre-join on error, stay in waiting room
      // Only redirect if the meeting was cancelled or ended
      if (err.message?.includes('cancelled') || err.message?.includes('ended')) {
        toast.error(err.message);
        setPageState('ended');
      }
    }
  };

  // Poll for admission when in waiting room and meeting is in progress
  // Note: We use polling here because the subscription only notifies on conference status changes,
  // not when a specific guest is admitted from the waiting room. When admitted, the backend
  // will return credentials instead of WAITING status.
  useEffect(() => {
    console.log('Polling useEffect - pageState:', pageState, 'conference?.status:', conference?.status);
    if (pageState !== 'waiting') return;
    
    // If meeting hasn't started yet, poll to check if it has started
    if (conference?.status === 'SCHEDULED') {
      console.log('Meeting not started yet, polling for status change...');
      const statusPoll = setInterval(() => {
        refetch();
      }, 3000);
      return () => clearInterval(statusPoll);
    }
    
    // Meeting is in progress, poll to check if we've been admitted
    if (conference?.status === 'IN_PROGRESS') {
      console.log('Starting admission polling...');
      
      const checkAdmission = async () => {
        console.log('Checking admission...');
        await autoJoinMeeting();
      };
      
      // Check immediately when meeting starts
      checkAdmission();
      
      // Then poll every 3 seconds to check admission status
      const interval = setInterval(checkAdmission, 3000);
      return () => {
        console.log('Stopping admission polling');
        clearInterval(interval);
      };
    }
    
    return; // No cleanup needed for other statuses
  }, [pageState, conference?.status, refetch]);

  // Subscribe to conference updates for real-time status changes
  const { data: updatedData } = useSubscription<OnVideoConferenceUpdatedData>(ON_VIDEO_CONFERENCE_UPDATED, {
    variables: { conferenceId: token || '' },
    skip: !token || pageState === 'ended',
    onData: ({ data: subData }) => {
      const updated = subData?.data?.onVideoConferenceUpdated;
      if (updated) {
        if (updated.status === 'IN_PROGRESS') {
          toast.success('The meeting has started! Joining now...', {
            duration: 3000,
            icon: '🎥',
          });
          // Refetch to get updated conference data
          refetch();
          // If in waiting room, auto-join
          if (pageState === 'waiting') {
            autoJoinMeeting();
          }
        } else if (updated.status === 'COMPLETED' || updated.status === 'CANCELLED') {
          // Meeting has ended - clear guest info and show ended screen
          clearStoredGuestInfo();
          setWaitingRoomGuestName('');
          setWaitingRoomGuestEmail('');
          setCredentials(null);
          toast('The meeting has ended.', {
            duration: 5000,
            icon: '✅',
          });
          setPageState('ended');
        }
      }
    },
  });

  // Suppress unused variable warnings - subscriptions are handled via onData callbacks
  void updatedData;

  // Handle network reconnection - refetch data when network comes back online
  useEffect(() => {
    const handleOnline = () => {
      // Network is back online, refetch to ensure data is fresh
      toast.success('Connection restored', { duration: 2000 });
      refetch();
    };

    const handleOffline = () => {
      toast.error('Connection lost. Trying to reconnect...', { duration: 4000 });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [refetch]);

  // Join error state for displaying errors on the page
  const [joinError, setJoinError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState('');

  // Email validation helper
  const isValidEmail = (email: string) => {
    // Stricter email regex - only allows alphanumeric, dots, hyphens, underscores, and plus signs
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  };

  // Handle join
  const handleJoin = async () => {
    let hasError = false;
    
    if (!guestName.trim()) {
      setNameError('Please enter your name');
      hasError = true;
    } else {
      setNameError('');
    }
    
    if (!guestEmail.trim()) {
      setEmailError('Please enter your email');
      hasError = true;
    } else if (!isValidEmail(guestEmail.trim())) {
      setEmailError('Please enter a valid email address');
      hasError = true;
    } else {
      setEmailError('');
    }
    
    if (hasError) return;
    
    setJoinError(null);

    // If meeting not started, go to waiting room and store name for auto-join
    if (conference?.status === 'SCHEDULED') {
      setWaitingRoomGuestName(guestName.trim());
      setWaitingRoomGuestEmail(guestEmail.trim());
      setPageState('waiting');
      return;
    }

    try {
      const { data } = await joinAsGuest({
        variables: {
          id: token,
          guestName: guestName.trim(),
          guestEmail: guestEmail.trim(),
        },
      });

      if (data?.joinVideoConferenceAsGuest) {
        const response = data.joinVideoConferenceAsGuest;
        
        // Check if we're being sent to waiting room
        if (response.status === 'WAITING') {
          setWaitingRoomGuestName(guestName.trim());
          setWaitingRoomGuestEmail(guestEmail.trim());
          setPageState('waiting');
          toast.success('You\'re in the waiting room. The host will admit you shortly.', { duration: 5000 });
          return;
        }
        
        // Clean up preview stream before joining call
        cleanupMedia();
        
        // Parse the meeting and attendee JSON from the backend
        const meeting = response.meetingJson ? JSON.parse(response.meetingJson) : null;
        const attendee = response.attendeeJson ? JSON.parse(response.attendeeJson) : null;
        
        if (!meeting || !attendee || !response.meetingId || !response.attendeeId) {
          throw new Error('Invalid meeting credentials received. Please refresh and try again.');
        }
        
        // Set credentials and transition to call
        setCredentials({
          meetingId: response.meetingId,
          mediaRegion: response.mediaRegion || 'us-east-1',
          attendeeId: response.attendeeId,
          joinToken: response.joinToken || '',
          externalUserId: response.externalUserId || '',
          meeting,
          attendee,
        });
        setPageState('in-call');
      } else {
        throw new Error('No response from server. Please try again.');
      }
    } catch (err: any) {
      console.error('Error joining conference:', err);
      const errorMsg = err.message || 'Failed to join. Please try again.';
      setJoinError(errorMsg);
      toast.error(errorMsg, { duration: 5000 });
    }
  };

  // Handle call end (meeting ended by host)
  const handleCallEnd = () => {
    clearStoredGuestInfo();
    setWaitingRoomGuestName('');
    setWaitingRoomGuestEmail('');
    setCredentials(null);
    setPageState('ended');
  };

  // Handle leave call (guest chose to leave)
  const handleLeaveCall = () => {
    clearStoredGuestInfo();
    setWaitingRoomGuestName('');
    setWaitingRoomGuestEmail('');
    setCredentials(null);
    setPageState('ended');
  };

  // Format date/time
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Get host name
  const getHostName = () => {
    if (conference?.host) {
      return `${conference.host.firstName} ${conference.host.lastName}`;
    }
    return 'Host';
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-white"></div>
          </div>
          <p className="text-white text-lg sm:text-xl">Loading conference...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 sm:w-12 sm:h-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-white mb-3">Unable to Join</h1>
          <p className="text-gray-400 text-base sm:text-lg mb-8 px-4">{errorMessage}</p>
          <p className="text-gray-500 text-sm px-4">
            If you believe this is an error, please contact the meeting host.
          </p>
        </div>
      </div>
    );
  }

  // Call ended state
  if (pageState === 'ended') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          {/* Icon */}
          <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          {/* Title */}
          <h1 className="text-2xl font-semibold text-white mb-3">This conference has ended</h1>
          
          {/* Conference info */}
          {conference && (
            <div className="bg-gray-800 rounded-lg p-4 mb-8 text-left">
              <p className="text-white font-medium">{conference.title}</p>
              <p className="text-gray-400 text-sm mt-1">Host: {getHostName()}</p>
            </div>
          )}
          
          {/* Close button */}
          <Button
            onClick={() => {
              // Try to close the window/tab
              window.close();
              // If window.close() doesn't work (e.g., not opened by script),
              // show a message or redirect
              setTimeout(() => {
                // If still here after 100ms, the close didn't work
                // Could redirect to a thank you page or show message
              }, 100);
            }}
            className="w-full bg-white text-gray-900 hover:bg-gray-100 py-3 text-lg font-medium"
          >
            Close Window
          </Button>
          
          <p className="text-gray-500 text-sm mt-4">
            If the window doesn't close, you can close this tab manually.
          </p>
        </div>
      </div>
    );
  }

  // Waiting room state
  if (pageState === 'waiting') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          {/* Animated waiting icon */}
          <div className="w-24 h-24 sm:w-28 sm:h-28 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6 relative">
            <div className="absolute inset-0 rounded-full border-4 border-blue-500/30 animate-ping"></div>
            <svg className="w-12 h-12 sm:w-14 sm:h-14 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          
          {/* Title */}
          <h1 className="text-2xl sm:text-3xl font-semibold text-white mb-3">
            Waiting to Join
          </h1>
          
          {/* Message */}
          <p className="text-gray-400 text-base sm:text-lg mb-8 px-4">
            {conference?.status === 'IN_PROGRESS' 
              ? "You'll be connected shortly..."
              : "The meeting hasn't started yet. You'll join automatically when it begins."}
          </p>
          
          {/* Conference info card */}
          {conference && (
            <div className="bg-gray-800 rounded-xl p-5 mb-8 text-left mx-2">
              <h2 className="text-white font-semibold text-lg mb-3">{conference.title}</h2>
              <div className="space-y-2">
                <div className="flex items-center gap-3 text-gray-400">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>Host: {getHostName()}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm">{formatDateTime(conference.scheduledStartTime)}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Status indicator */}
          <div className="flex items-center justify-center gap-3 text-gray-500 mb-6">
            <div className="flex gap-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
            <span className="text-sm">Waiting for host</span>
          </div>
          
          {/* Go back button */}
          <Button
            variant="outline"
            onClick={() => {
              setUserWentBack(true);
              clearStoredGuestInfo();
              setWaitingRoomGuestName('');
              setWaitingRoomGuestEmail('');
              setPageState('pre-join');
            }}
            className="bg-transparent border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white px-8 py-3"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // In-call state
  if (pageState === 'in-call' && credentials && conference) {
    return (
      <MeetingProvider>
        <VideoCallWrapper
          credentials={credentials}
          conferenceId={conference.id}
          conferenceTitle={conference.title}
          isHost={false}
          isRecordingEnabled={conference.isRecordingEnabled}
          currentUserId={`guest-${waitingRoomGuestName || guestName}`}
          currentUserName={waitingRoomGuestName || guestName || 'Guest'}
          currentUserRole="GUEST"
          onEndCall={handleCallEnd}
          onLeaveCall={handleLeaveCall}
        />
      </MeetingProvider>
    );
  }

  // Pre-join screen
  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Mobile-first layout: stacked on mobile, side-by-side on desktop */}
      
      {/* Video Preview Section */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8 min-h-[40vh] lg:min-h-0">
        <div className="w-full max-w-2xl">
          {/* Video Preview */}
          <div className="relative aspect-video bg-gray-800 rounded-xl overflow-hidden mb-4">
            {isCameraOn ? (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover transform scale-x-[-1]"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-700 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              </div>
            )}
            
            {/* Camera/Mic status overlay */}
            <div className="absolute bottom-3 left-3 flex items-center gap-2">
              {!isCameraOn && (
                <Badge variant="destructive" className="text-xs">Camera Off</Badge>
              )}
              {!isMicOn && (
                <Badge variant="destructive" className="text-xs">Mic Off</Badge>
              )}
            </div>
          </div>

          {/* Device Controls - larger touch targets for mobile */}
          <div className="flex items-center justify-center gap-4 sm:gap-6">
            {/* Camera Toggle */}
            <button
              onClick={toggleCamera}
              className={`w-14 h-14 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-colors ${
                isCameraOn ? 'bg-gray-700 hover:bg-gray-600 active:bg-gray-500' : 'bg-red-600 hover:bg-red-700 active:bg-red-800'
              }`}
              title={isCameraOn ? 'Turn off camera' : 'Turn on camera'}
            >
              {isCameraOn ? (
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              )}
            </button>

            {/* Mic Toggle */}
            <button
              onClick={toggleMic}
              className={`w-14 h-14 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-colors ${
                isMicOn ? 'bg-gray-700 hover:bg-gray-600 active:bg-gray-500' : 'bg-red-600 hover:bg-red-700 active:bg-red-800'
              }`}
              title={isMicOn ? 'Mute microphone' : 'Unmute microphone'}
            >
              {isMicOn ? (
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              )}
            </button>
          </div>

          {/* Audio Level Indicator - hidden on mobile for cleaner UI */}
          {isMicOn && (
            <div className="hidden sm:flex mt-4 items-center justify-center gap-2">
              <span className="text-sm text-gray-400">Mic Level:</span>
              <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all duration-100"
                  style={{ width: `${audioLevel}%` }}
                />
              </div>
            </div>
          )}

          {/* Device Selectors - hidden on mobile */}
          <div className="hidden lg:flex mt-4 items-center justify-center gap-4">
            {videoDevices.length > 1 && (
              <select
                value={selectedVideoDevice}
                onChange={(e) => switchVideoDevice(e.target.value)}
                className="h-10 px-3 text-sm bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {videoDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </option>
                ))}
              </select>
            )}
            {audioDevices.length > 1 && (
              <select
                value={selectedAudioDevice}
                onChange={(e) => switchAudioDevice(e.target.value)}
                className="h-10 px-3 text-sm bg-gray-700 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {audioDevices.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Join Form Section - slides up from bottom on mobile */}
      <div className="w-full lg:w-96 lg:absolute lg:right-0 lg:top-0 lg:bottom-0 bg-white rounded-t-3xl lg:rounded-none p-6 sm:p-8 flex flex-col shadow-2xl lg:shadow-none">
        {/* Logo - smaller on mobile */}
        <div className="flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-aalaw-red rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm sm:text-lg">AA</span>
          </div>
          <span className="text-base sm:text-lg font-semibold text-gray-900">Video Conference</span>
        </div>

        {/* Conference Info */}
        {conference && (
          <div className="mb-5 sm:mb-6">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{conference.title}</h1>
            <div className="space-y-1.5 sm:space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>Host: {getHostName()}</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-xs sm:text-sm">{formatDateTime(conference.scheduledStartTime)}</span>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Badge variant={conference.status === 'IN_PROGRESS' ? 'success' : 'info'} className="text-xs">
                  {conference.status === 'IN_PROGRESS' ? 'In Progress' : 'Scheduled'}
                </Badge>
                {conference.isRecordingEnabled && (
                  <Badge variant="warning" className="text-xs">
                    Recording
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Join Form */}
        <div className="flex-1">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Your Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={guestName}
                onChange={(e) => {
                  setGuestName(e.target.value);
                  if (nameError) setNameError('');
                }}
                placeholder="Enter your name"
                className={`h-12 text-base ${nameError ? 'border-red-500' : ''}`}
              />
              {nameError && (
                <p className="text-sm text-red-500 mt-1">{nameError}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                value={guestEmail}
                onChange={(e) => {
                  setGuestEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                placeholder="Enter your email"
                className={`h-12 text-base ${emailError ? 'border-red-500' : ''}`}
              />
              {emailError && (
                <p className="text-sm text-red-500 mt-1">{emailError}</p>
              )}
            </div>
          </div>
        </div>

        {/* Join Button - larger for mobile */}
        <div className="mt-6 pb-safe">
          <Button
            onClick={handleJoin}
            disabled={joining || !guestName.trim() || !guestEmail.trim()}
            className="w-full h-14 text-base sm:text-lg font-medium"
          >
            {joining ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Joining...
              </>
            ) : conference?.status === 'SCHEDULED' ? (
              'Join Waiting Room'
            ) : (
              'Join Meeting'
            )}
          </Button>
          
          {/* Show join error if any */}
          {joinError && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{joinError}</p>
            </div>
          )}
          
          <p className="text-xs text-gray-500 text-center mt-2">
            {conference?.status === 'SCHEDULED' 
              ? "The meeting hasn't started yet. You'll be notified when the host begins."
              : "You'll be placed in the waiting room until the host admits you."}
          </p>

          {conference?.isRecordingEnabled && (
            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-amber-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="8" />
              </svg>
              <span>This meeting may be recorded</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            By joining, you agree to our terms of service and privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
}
